/**
 * avatarColor.ts
 *
 * Deterministic pid -> avatar gradient hash. Cycles through the same
 * green/amber/cyan/violet family already used for priority type/status
 * tokens so avatars feel consistent with the rest of the design system,
 * without inventing a new palette.
 */

const AVATAR_GRADIENTS = [
  "bg-gradient-to-br from-green-500 to-green-600 dark:from-green-500 dark:to-green-700",
  "bg-gradient-to-br from-amber-500 to-amber-600 dark:from-amber-500 dark:to-amber-700",
  "bg-gradient-to-br from-cyan-500 to-cyan-600 dark:from-cyan-500 dark:to-cyan-700",
  "bg-gradient-to-br from-violet-500 to-violet-600 dark:from-violet-500 dark:to-violet-700",
] as const;

/** Simple deterministic string hash — stable across renders/sessions. */
function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** Tailwind gradient classes for an avatar, hashed deterministically by `pid`. */
export function getAvatarGradient(pid: string): string {
  if (!pid) return AVATAR_GRADIENTS[0];
  return AVATAR_GRADIENTS[hashString(pid) % AVATAR_GRADIENTS.length];
}

/** Up to 2 uppercase initials to render inside an avatar. */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
